import React, { useEffect, useMemo, useState } from "react";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import dayjs from "dayjs";
import Sidebar from "../Sidebar";
import Layout from "../../layout/Layout";
import { GET_CANCELLATION_POLICY, GET_CANCELLATION_POLICY_VERSION, SAVE_CANCELLATION_POLICY } from "./graphql";
import { inputCls } from "./controls";
import PolicyRules from "./PolicyRules";
import { PolicyCollection, PolicyCompensation, PolicyExemptions, PolicyWording } from "./PolicySettings";
import ChargesRecord from "./ChargesRecord";

// Cancellation charges: the rules (each save is a new version), exemptions,
// how fees get collected, Barbera's credits, the customer wording, and the
// fees & credits record. Engine: barbera_backend src/services/cancellation-policy.

const TABS = [
  { key: "rules", label: "Rules" },
  { key: "exemptions", label: "Exemptions" },
  { key: "collection", label: "Collection" },
  { key: "credits", label: "Barbera credits" },
  { key: "wording", label: "Customer wording" },
  { key: "record", label: "Fees & credits" },
  { key: "history", label: "History" },
];

const MODES = [
  { key: "OFF", label: "Off", hint: "Nothing is recorded or charged." },
  { key: "SHADOW", label: "Shadow", hint: "Every cancellation is judged and recorded. Nobody is charged and customers see nothing." },
  { key: "LIVE", label: "Live", hint: "Customers see the policy at checkout; fees are charged on bookings that accept it." },
];

const MODE_TONE = { OFF: "bg-gray-200 text-gray-700", SHADOW: "bg-amber-100 text-amber-800", LIVE: "bg-emerald-100 text-emerald-800" };

const clone = (o) => JSON.parse(JSON.stringify(o));

const CancellationPolicy = () => {
  const { data, loading, refetch } = useQuery(GET_CANCELLATION_POLICY, { fetchPolicy: "network-only" });
  const [save, { loading: saving }] = useMutation(SAVE_CANCELLATION_POLICY);
  const [loadVersion] = useLazyQuery(GET_CANCELLATION_POLICY_VERSION, { fetchPolicy: "no-cache" });
  const res = data?.getCancellationPolicy;
  const current = res?.current;
  const saved = useMemo(() => (res ? clone(current?.policy || res.defaults) : null), [res, current]);
  const savedMode = current?.mode || "OFF";
  const liveReady = !!res?.options?.liveReady;

  const [tab, setTab] = useState("rules");
  const [draft, setDraft] = useState(null);
  const [mode, setMode] = useState("OFF");
  const [note, setNote] = useState("");
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (saved) { setDraft(clone(saved)); setMode(savedMode); }
  }, [saved, savedMode]);

  const dirty = draft && saved && (JSON.stringify(draft) !== JSON.stringify(saved) || mode !== savedMode);

  // Unsaved changes survive a tab switch but not leaving the page.
  useEffect(() => {
    const warn = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const onSave = async () => {
    setMsg(null);
    if (mode === "LIVE" && savedMode !== "LIVE" && !window.confirm(
      "Go Live? Customers will see this policy at checkout and on their bookings, and fees will be charged on bookings that accept it (from refunds, wallet, or as dues). Bookings made before - or from older app versions - are never charged."
    )) return;
    const { data: d } = await save({ variables: { policy: draft, mode, note: note.trim() || null } });
    const r = d?.saveCancellationPolicy;
    if (r?.status === "SUCCESS") {
      setNote("");
      setMsg({ ok: true, text: `${r.message}. ${mode === "SHADOW" ? "New cancellations are now judged by it." : ""}` });
      refetch();
    } else setMsg({ ok: false, text: r?.message || "Could not save." });
  };

  const restore = async (id, version) => {
    if (dirty && !window.confirm("Discard your unsaved changes?")) return;
    const { data: d } = await loadVersion({ variables: { id } });
    const v = d?.getCancellationPolicyVersion?.version;
    if (!v) return setMsg({ ok: false, text: "Could not load that version." });
    setDraft(clone(v.policy));
    setMode(v.mode === "LIVE" && !liveReady ? "SHADOW" : v.mode);
    setNote(`Restored version ${version}`);
    setTab("rules");
    setMsg({ ok: true, text: `Version ${version} loaded into the editor - save to make it current.` });
  };

  const editing = tab !== "record" && tab !== "history";

  return (
    <div className="min-h-screen flex">
      <Sidebar />
      <Layout>
        <div className="max-w-7xl space-y-5 pb-24">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h1 className="text-2xl font-semibold text-white flex items-center gap-2">
                Cancellation Policy
                {current && <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${MODE_TONE[savedMode]}`}>{savedMode.charAt(0) + savedMode.slice(1).toLowerCase()}</span>}
              </h1>
              <p className="text-sm text-gray-400 mt-0.5">
                {current
                  ? `Version ${current.version} · saved ${dayjs(current.createdAt).format("DD MMM YYYY, h:mm A")}${current.createdBy ? ` by ${current.createdBy}` : ""}`
                  : "Not saved yet - showing the suggested defaults. Save in Shadow to start recording."}
              </p>
            </div>
          </div>

          {res?.status === "ERROR" && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">{res.message}</div>}
          {msg && <div className={`rounded-xl px-4 py-3 text-sm border ${msg.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-700"}`}>{msg.text}</div>}

          <div className="grid sm:grid-cols-3 gap-2">
            {MODES.map((m) => {
              const disabled = m.key === "LIVE" && !liveReady;
              return (
                <button
                  key={m.key}
                  type="button"
                  disabled={disabled}
                  onClick={() => setMode(m.key)}
                  title={disabled ? "Turns on once the customer app shows the policy (next app release)" : undefined}
                  className={`text-left rounded-xl border p-3 transition ${mode === m.key ? "border-[#FBB033] bg-[#FBB033]/10" : "border-white/10 bg-white/5 hover:bg-white/10"} ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
                >
                  <p className="text-sm font-semibold text-white">{m.label}{disabled && <span className="text-xs font-normal text-gray-400"> · after app release</span>}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{m.hint}</p>
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap gap-1 border-b border-white/10">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`px-3 py-2 text-sm font-semibold -mb-px border-b-2 ${tab === t.key ? "border-[#FBB033] text-[#FBB033]" : "border-transparent text-gray-300 hover:text-white"}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {loading && !draft && <p className="text-sm text-gray-400">Loading…</p>}
          {draft && tab === "rules" && <PolicyRules policy={draft} onChange={setDraft} />}
          {draft && tab === "exemptions" && <PolicyExemptions policy={draft} onChange={setDraft} />}
          {draft && tab === "collection" && <PolicyCollection policy={draft} onChange={setDraft} />}
          {draft && tab === "credits" && <PolicyCompensation policy={draft} onChange={setDraft} />}
          {draft && tab === "wording" && <PolicyWording policy={draft} onChange={setDraft} />}
          {tab === "record" && <ChargesRecord />}
          {tab === "history" && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden max-w-4xl">
              {!(res?.versions || []).length && <p className="p-4 text-sm text-gray-400">No versions saved yet.</p>}
              {(res?.versions || []).map((v) => (
                <div key={v.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-gray-100 first:border-t-0">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      Version {v.version}
                      <span className={`ml-2 text-[11px] px-2 py-0.5 rounded-full ${MODE_TONE[v.mode]}`}>{v.mode.toLowerCase()}</span>
                      {current?.id === v.id && <span className="ml-1 text-[11px] text-emerald-700">current</span>}
                    </p>
                    <p className="text-xs text-gray-500">{dayjs(v.createdAt).format("DD MMM YYYY, h:mm A")}{v.createdBy ? ` · ${v.createdBy}` : ""}{v.note ? ` · ${v.note}` : ""}</p>
                  </div>
                  {current?.id !== v.id && (
                    <button type="button" onClick={() => restore(v.id, v.version)} className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50">
                      Load into editor
                    </button>
                  )}
                </div>
              ))}
              <p className="px-4 py-3 text-xs text-gray-500 bg-gray-50">Each booking is judged by the version in force when it was made, so saving a change never re-prices existing bookings.</p>
            </div>
          )}
        </div>

        {editing && draft && (dirty || !current) && (
          <div className="fixed bottom-0 right-0 left-0 lg:left-72 z-30 bg-[#0C0B0B]/95 border-t border-white/10 px-6 py-3 flex flex-wrap items-center justify-end gap-3">
            <span className="text-sm text-gray-300 mr-auto">{current ? "Unsaved changes" : "Nothing saved yet"} · saves as version {(current?.version || 0) + 1} in <b>{mode.toLowerCase()}</b> mode</span>
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="What changed? (optional)" className={`${inputCls} w-64`} />
            {current && (
              <button type="button" onClick={() => { setDraft(clone(saved)); setMode(savedMode); setNote(""); }} className="px-3 py-2 rounded-lg text-sm text-gray-300 hover:text-white">
                Discard
              </button>
            )}
            <button type="button" disabled={saving} onClick={onSave} className="px-4 py-2 rounded-lg bg-[#FBB033] text-gray-900 text-sm font-semibold disabled:opacity-50">
              {saving ? "Saving…" : "Save new version"}
            </button>
          </div>
        )}
      </Layout>
    </div>
  );
};

export default CancellationPolicy;
